import React from 'react';
import Shield from '../../../shared/components/Shield';
import TrafficButtons from '../../../shared/components/TrafficButtons';
import IconBox from '../../../shared/components/IconBox';
import Indicator from '../../../shared/components/Indicator';
import ScrollingText from '../../../shared/components/ScrollingText';
import { useMobile } from '../../../contexts/MobileContext';

import { ReactComponent as TrashIcon } from '../../../assets/icons/trash.svg';
import { ReactComponent as DragHandleIcon } from '../../../assets/icons/dragHandle.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { Pill, fraudPillColors, gridTemplateSettings, useViewport, useWidth } from '../../../shared/components/tableUtils';

export default function SettingsSiteRow({site, setDeleteSiteModalVisible, setEditedIndex, setSiteIndex, index, dragHandleProps }){
  const { isMobile } = useMobile();
  const { vw } = useViewport();
  const isStacked = vw <= 640;
  const status = site?.status || null;
  const fraudLevel = site?.fraudLevel || null;
  const hasFraudLevel = Boolean(fraudLevel);
  const showFraudLevel = hasFraudLevel && status !== 'red';
  const [fraudRef, fraudW] = useWidth();
  const fraudLong = fraudW >= 220 && !(isMobile || vw <= 980);
  const showBotActivity = fraudW >= 280 && !(isMobile || vw <= 980);
  const gridTemplate = gridTemplateSettings(isMobile, vw);
  const rowStyle = { gridTemplateColumns:gridTemplate };
  const openDelete = () => {
    setSiteIndex(index);
    setDeleteSiteModalVisible(true);
  };
  const openEdit = () => setEditedIndex(index);

  const fraudContent = (
    <>
      {status === null && !hasFraudLevel && (
        <span>Unavailable</span>
      )}
      {status === 'red' && (
        <Pill text={isStacked ? 'Waiting' : (fraudLong ? 'Waiting for traffic' : 'Waiting')}
              bg={'var(--surface-contrast-low)'}
              fg={'var(--text-contrast-mid)'} />
      )}
      {showFraudLevel && (
        <div className="site-card__fraud-content">
          {!(isMobile && vw <= 360) && fraudW >= 120 ? (
            <Shield fraudLevel={fraudLevel}
                    style={{width:'min(1.2rem, 5vw)'}}/>
          ) : null}
          {(() => {
            const {bg,fg} = fraudPillColors(fraudLevel);
            return (
              <Pill text={String(fraudLevel)}
                    bg={bg}
                    fg={fg}/>
            );
          })()}
          {!isStacked && showBotActivity && (
            <small className="site-card__muted-note">
              bot activity
            </small>
          )}
        </div>
      )}
    </>
  );

  if (isStacked) {
    return (
      <div className="site-card site-card--settings-mobile">
        <div {...dragHandleProps}
             className="drag-handle site-card__mobile-drag">
          <DragHandleIcon className="icon xs drag-handle-icon" />
        </div>

        <IconBox website={site.website} className="site-card__mobile-favicon" />

        <div className="site-card__mobile-url">{site.website}</div>

        <div className="site-card__mobile-actions">
          <button type="button"
                  className="btn btn-icon site-card__mobile-action"
                  aria-label={`Edit ${site.website}`}
                  onClick={openEdit}>
            <EditIcon className="icon sm" />
          </button>

          <button type="button"
                  className="btn destructive compact icon-only site-card__mobile-action"
                  aria-label={`Delete ${site.website}`}
                  onClick={openDelete}>
            <TrashIcon className="icon sm" />
          </button>
        </div>

        <div className="site-card__mobile-meta">
          <span className="subtle-label site-card__mobile-label">Traffic</span>
          <span className="subtle-label site-card__mobile-label">Fraud</span>
          <span className="subtle-label site-card__mobile-label">Status</span>

          <div className="site-card__mobile-indicator">
            <Indicator className="site-card__indicator" site={site}/>
          </div>

          <div ref={fraudRef}
               className="site-card__mobile-fraud">
            {fraudContent}
          </div>

          <div className="site-card__mobile-traffic">
            <TrafficButtons direction="row" site={site} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="site-card site-card--row site-card--settings-row" style={rowStyle}>
      <div className="site-card__cell site-card__cell--center">
        <div {...dragHandleProps}
             className="drag-handle">
          <DragHandleIcon className="icon xs drag-handle-icon" />
        </div>
      </div>
      <div className="site-card__cell site-card__cell--center">
        <IconBox website={site.website} />
      </div>
      <div className="site-card__cell site-card__site">
        <ScrollingText className="site-card__name" text={site.website} />
      </div>
      <div className="site-card__cell site-card__cell--center">
        <Indicator className="site-card__indicator" site={site}/>
      </div>
      <div ref={fraudRef}
           className="site-card__cell site-card__cell--fraud">
        {status === null && !hasFraudLevel && (
          <span>Unavailable</span>
        )}
        {status === 'red' && (
          <Pill text={(isMobile || vw <= 980) ? 'Waiting' : (fraudLong ? 'Waiting for traffic' : 'Waiting')}
                bg={'var(--surface-contrast-low)'}
                fg={'var(--text-contrast-mid)'} />
        )}
        {showFraudLevel && (
          <div className="site-card__fraud-content">
             {!(isMobile && vw <= 360) && fraudW >= 120 ? (
                          <Shield fraudLevel={fraudLevel}
                                  style={{width:'min(1.2rem, 5vw)'}}/>
                        ) : null}
            {(()=>{const {bg,fg}=fraudPillColors(fraudLevel);
                   return <Pill text={String(fraudLevel)}
                         bg={bg}
                         fg={fg}/>;
            })()}
            {showBotActivity && (
              <small className="site-card__muted-note">
                bot activity
              </small>
            )}
          </div>
        )}
      </div>
      <div className="site-card__cell site-card__cell--status">
        <TrafficButtons site={site} />
      </div>
      <div className="site-card__actions">
        <button type="button"
                className="btn btn-icon site-card__action-btn"
                aria-label={`Edit ${site.website}`}
                onClick={openEdit}>
          <EditIcon className="icon sm" />
        </button>

        <button type="button"
                className="btn destructive compact icon-only site-card__action-btn"
                aria-label={`Delete ${site.website}`}
                onClick={openDelete}>
          <TrashIcon className="icon sm" />
        </button>
      </div>
    </div>
  );
}
